import { TypeProduct } from '@/core/generated-types';
import { getStoreRegion } from '@/utils/environment';
import { Region } from '@/utils/translations/types/TypeLanguagesAndLocales';
import { Category } from '@/components/common/MultiLevelSelector/types';
import { KIT_ANCHORS } from '@/components/subflow-v3/components/utils';

export enum PlaykitYear {
  Year0 = 'year-0',
  Year1 = 'year-1',
  Year2 = 'year-2',
  Year3 = 'year-3',
  Year4 = 'year-4',
}

export const PlaykitAnchorToYear: Record<string, PlaykitYear> = {
  [KIT_ANCHORS.LOOKER]: PlaykitYear.Year0,
  [KIT_ANCHORS.CHARMER]: PlaykitYear.Year0,
  [KIT_ANCHORS.SENSER]: PlaykitYear.Year0,
  [KIT_ANCHORS.INSPECTOR]: PlaykitYear.Year0,
  [KIT_ANCHORS.EXPLORER]: PlaykitYear.Year0,
  [KIT_ANCHORS.THINKER]: PlaykitYear.Year0,

  [KIT_ANCHORS.BABBLER]: PlaykitYear.Year1,
  [KIT_ANCHORS.ADVENTURER]: PlaykitYear.Year1,
  [KIT_ANCHORS.REALIST]: PlaykitYear.Year1,
  [KIT_ANCHORS.COMPANION]: PlaykitYear.Year1,

  [KIT_ANCHORS.HELPER]: PlaykitYear.Year2,
  [KIT_ANCHORS.ENTHUSIAST]: PlaykitYear.Year2,
  [KIT_ANCHORS.RESEARCHER]: PlaykitYear.Year2,
  [KIT_ANCHORS.FREE_SPIRIT]: PlaykitYear.Year2,

  [KIT_ANCHORS.OBSERVER]: PlaykitYear.Year3,
  [KIT_ANCHORS.STORYTELLER]: PlaykitYear.Year3,
  [KIT_ANCHORS.PROBLEM_SOLVER]: PlaykitYear.Year3,
  [KIT_ANCHORS.ANALYST]: PlaykitYear.Year3,

  [KIT_ANCHORS.CONNECTOR]: PlaykitYear.Year4,
  [KIT_ANCHORS.EXAMINER]: PlaykitYear.Year4,
  [KIT_ANCHORS.PERSISTER]: PlaykitYear.Year4,
  [KIT_ANCHORS.PLANNER]: PlaykitYear.Year4,
};

export const SUBFLOW_STARTING_KIT_KEY = 'subflow-starting-kit-anchor';

export const PKLPUrlToStartingAnchor: Record<string, string> = {
  baby: KIT_ANCHORS.LOOKER,
  '1-year-old': KIT_ANCHORS.BABBLER,
  '2-year-old': KIT_ANCHORS.HELPER,
  '3-year-old': KIT_ANCHORS.OBSERVER,
  '4-year-old': KIT_ANCHORS.CONNECTOR,
};

export const SectionIndexToStartingAnchor: Record<string, string> = {
  0: KIT_ANCHORS.LOOKER,
  1: KIT_ANCHORS.BABBLER,
  2: KIT_ANCHORS.HELPER,
  3: KIT_ANCHORS.OBSERVER,
  4: KIT_ANCHORS.CONNECTOR,
};

export function buildPlayKitAgeSelectorCategories(t: Record<string, string>) {
  const region = getStoreRegion();

  const year0To3Kits: Category[] = [
    {
      label: t.year0Label || '0-12 Months',
      value: PlaykitYear.Year0,
      subCategoryColumnCount: 2,
    },
    {
      label: t.year1Label || 'Year 1',
      value: PlaykitYear.Year1,
      subCategoryColumnCount: 2,
    },
    {
      label: t.year2Label || 'Year 2',
      value: PlaykitYear.Year2,
      subCategoryColumnCount: 2,
    },
    {
      label: t.year3Label || 'Year 3',
      value: PlaykitYear.Year3,
      subCategoryColumnCount: 2,
    },
  ];

  const year0to4Kits: Category[] = [
    ...year0To3Kits,
    {
      label: t.year4Label || 'Year 4',
      value: PlaykitYear.Year4,
      subCategoryColumnCount: 2,
    },
  ];

  switch (region) {
    case Region.EU:
    case Region.UK:
    case Region.AU:
      return year0To3Kits;

    case Region.US:
    default:
      return year0to4Kits;
  }
}

export function getPlayKitAgeSelectorCategories(
  products: TypeProduct[],
  t: Record<string, string>,
) {
  const categories = buildPlayKitAgeSelectorCategories(t);
  const categoryToSubCategoriesMap: Record<string, Category[]> = {};

  categories.forEach((category) => {
    categoryToSubCategoriesMap[category.value] = [];
  });

  products.forEach((product) => {
    const { playgeniusProductAnchor, ageRange } = product.fields;

    if (playgeniusProductAnchor && ageRange) {
      const productCategory = PlaykitAnchorToYear[playgeniusProductAnchor];

      categoryToSubCategoriesMap[productCategory]?.push({
        label: ageRange,
        value: playgeniusProductAnchor,
      });
    }
  });

  return { categories, categoryToSubCategoriesMap };
}
